// src/pages/Contact.jsx
// import React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Option A: public/ path (put file at public/images/world2.png)
const WORLD_IMG_PUBLIC = "./images/world.png"; // root-served path [web:12][web:24]

// Option B: src import (put file at src/assets/world2.png)
// import world2 from "../assets/world2.png"; // bundler-managed asset [web:12][web:33]

const PALETTE = {
  ivory: "#F7F1E8",
  charcoal: "#1B1B1B",
  tan: "#593c24",
  tanHover: "#7a4f2e",
  textDark: "#2A2A2A",
  textLight: "#FFFFFF",
  subDark: "#6C6C6C",
  subLight: "#D2D2D2",
  lineLight: "#DCD2C5",
  lineDark: "rgba(255,255,255,0.18)",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
  },
};



export default function Contact() {
  // Toggle the one used below:
  const WORLD_IMG = WORLD_IMG_PUBLIC; // use "/images/world2.png" from public [web:12][web:24]
  // const WORLD_IMG = world2; // use imported src asset [web:12][web:33]

  // Form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Use relative path to PHP endpoint on shared hosting
      const res = await fetch("/sendmail.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Submission failed");
      }
      setShowModal(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className=" bg-[#e3dbcb]/[91%] relative w-full overflow-hidden pt-[65px]"
      style={{
        // backgroundColor: '',
        color: PALETTE.textDark,
      }}
    >
      {/* Non-stretch, clear image layer using <img> so it always shows if path is correct */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <img
          src={WORLD_IMG}
          alt=""
          className="
            h-full w-full
            object-contain
            object-center
            opacity-30
            mix-blend-lighten
            select-none
          "
        />
      </div>

      {/* If preferring CSS background instead of <img>, use this block instead:
      <div
        className="
          absolute inset-0 -z-10
          bg-no-repeat bg-center bg-contain
          opacity-30 mix-blend-lighten
        "
        style={{
          // Option A: public
          backgroundImage: "url('/images/world2.png')", // root path [web:12][web:21]
          // Option B: src import
          // backgroundImage: `url(${WORLD_IMG})`, // when WORLD_IMG is an import [web:12][web:33]
        }}
      />
      */}

      <div className="relative mx-auto flex h-full max-w-6xl flex-col px-6 lg:flex-row lg:items-stretch lg:gap-16">
        {/* Right form panel */}
        <div className="relative mt-12 mb-12 w-full flex-1">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="relative z-10 mx-auto w-full max-w-[760px] rounded-[14px] p-10 backdrop-blur-[2px]"
            style={{
              // backgroundColor: "#222222",
              borderColor: PALETTE.lineLight,
              color: PALETTE.textDark,
            }}
          >
            <h2 className="text-2xl font-semibold" style={{ color: PALETTE.textDark }}>
              Let's Create Together
            </h2>
            <p className="mt-1 text-sm" style={{ color: PALETTE.subDark }}>
              We'd love to hear from you!
            </p>

            <form className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2" onSubmit={handleSubmit}>
              <div className="md:col-span-1">
                <label className="mb-2 block text-xs uppercase tracking-[0.25em]" style={{ color: PALETTE.subDark }}>Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full rounded-md border px-4 py-3 text-sm outline-none ring-0 transition-colors"
                  placeholder="Full name"
                  style={{ backgroundColor: PALETTE.ivory, color: PALETTE.textDark, borderColor: PALETTE.lineLight }}
                  onFocus={e => { e.currentTarget.style.borderColor = PALETTE.tan; }}
                  onBlur={e => { e.currentTarget.style.borderColor = PALETTE.lineLight; }}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className="mb-2 block text-xs uppercase tracking-[0.25em]" style={{ color: PALETTE.subDark }}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="email@example.com"
                  style={{ backgroundColor: PALETTE.ivory, color: PALETTE.textDark, borderColor: PALETTE.lineLight }}
                  onFocus={e => { e.currentTarget.style.borderColor = PALETTE.tan; }}
                  onBlur={e => { e.currentTarget.style.borderColor = PALETTE.lineLight; }}
                  required
                />
              </div>

              <div className="md:col-span-1">
                <label className="mb-2 block text-xs uppercase tracking-[0.25em]" style={{ color: PALETTE.subDark }}>Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="+91 00000 00000"
                  style={{ backgroundColor: PALETTE.ivory, color: PALETTE.textDark, borderColor: PALETTE.lineLight }}
                  onFocus={e => { e.currentTarget.style.borderColor = PALETTE.tan; }}
                  onBlur={e => { e.currentTarget.style.borderColor = PALETTE.lineLight; }}
                />
              </div>

              <div className="md:col-span-1">
                <label className="mb-2 block text-xs uppercase tracking-[0.25em]" style={{ color: PALETTE.subDark }}>Subject</label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  className="w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="Project type"
                  style={{ backgroundColor: PALETTE.ivory, color: PALETTE.textDark, borderColor: PALETTE.lineLight }}
                  onFocus={e => { e.currentTarget.style.borderColor = PALETTE.tan; }}
                  onBlur={e => { e.currentTarget.style.borderColor = PALETTE.lineLight; }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-xs uppercase tracking-[0.25em]" style={{ color: PALETTE.subDark }}>Your message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  className="w-full h-24 resize-none rounded-md border px-4 py-3 text-sm outline-none transition-colors"
                  placeholder="Tell us a bit about the space…"
                  style={{ backgroundColor: PALETTE.ivory, color: PALETTE.textDark, borderColor: PALETTE.lineLight }}
                  onFocus={e => { e.currentTarget.style.borderColor = PALETTE.tan; }}
                  onBlur={e => { e.currentTarget.style.borderColor = PALETTE.lineLight; }}
                  required
                />
              </div>

              {error && (
                <div className="md:col-span-2 text-red-400 text-sm">{error}</div>
              )}

              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold transition-colors"
                  style={{ backgroundColor: PALETTE.tan, color: PALETTE.textLight }}
                  onMouseEnter={e => { 
                    e.currentTarget.style.backgroundColor = PALETTE.tanHover;
                  }}
                  onMouseLeave={e => { 
                    e.currentTarget.style.backgroundColor = PALETTE.tan;
                  }}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Get in Touch"}
                </button>
              </div>
            </form>
            {/* Modal Popup */}
            {showModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal-900 bg-opacity-60">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Thank you for your interest in MANAR – Storyteller of Spaces.</h3>
                  <p className="mb-4 text-gray-800">Your details have been successfully submitted. Our team will get in touch with you soon.</p>
                  <Link
                    to="/portfolio"
                    className="inline-block bg-[#C3A585] hover:bg-[#b59473] text-[#2A2A2A] font-semibold px-6 py-2 rounded-md transition-colors mb-2"
                    onClick={() => setShowModal(false)}
                  >
                    Explore Our Projects
                  </Link>
                  <button
                    className="block w-full mt-2 text-sm text-gray-500 hover:text-gray-700"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Left contact card */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="relative z-10 mt-12 mb-12 w-full rounded-[14px] p-10 shadow-[0_10px_40px_rgba(0,0,0,0.15)] lg:max-w-[520px]"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            borderColor: PALETTE.lineLight,
            color: PALETTE.textDark,
          }}
        >
          <div className="relative z-10">
            <h1 className="text-4xl font-extrabold tracking-tight">
              <span style={{ color: PALETTE.tan }}>Contact</span> us
            </h1>
            <p className="mt-2 text-sm" style={{ color: PALETTE.subDark }}>
              Ask for a quotation from us!
            </p>

            <div className="mt-8 space-y-6">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: PALETTE.tan,
                    color: PALETTE.ivory,
                  }}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 6.75c0-1.242 1.008-2.25 2.25-2.25h2.167c.96 0 1.79.654 2.037 1.582l.58 2.176a2.25 2.25 0 01-.516 2.053L7.6 11.384a16.5 16.5 0 006.016 6.016l1.073-1.168a2.25 2.25 0 012.053-.516l2.176.58a2.25 2.25 0 011.582 2.037V21c0 1.242-1.008 2.25-2.25 2.25h-.75C8.3 23.25.75 15.7.75 6.75z"
                    />
                  </svg>
                </span>
                <span className="text-sm" style={{ color: PALETTE.textDark }}>
                  8767146842
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: PALETTE.tan,
                    color: PALETTE.ivory,
                  }}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 6.75l9.75 6.75L21.75 6.75M3 6.75h18v10.5A2.25 2.25 0 0118.75 19.5H5.25A2.25 2.25 0 013 17.25V6.75z"
                    />
                  </svg>
                </span>
                <span className="text-sm" style={{ color: PALETTE.textDark }}>
                  manar.designs1@gmail.com
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: PALETTE.tan,
                    color: PALETTE.ivory,
                  }}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 10.5c0 7.5-7.5 11.25-7.5 11.25S4.5 18 4.5 10.5a7.5 7.5 0 1115 0z"
                    />
                  </svg>
                </span>
                <span className="text-sm" style={{ color: PALETTE.textDark }}>
                  Office Number A 112, 1st Floor, Bizz Bay Mall, NIBM Road, Pune - 411048
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
